import {
  Building2,
  ChevronDown,
  LogOut,
  Menu,
  Moon,
  Settings,
  Sun,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";

import { useAuth } from "../auth/AuthContext";
import Can from "../auth/Can";
import ContextSwitcherMenu from "../components/ContextSwitcherMenu";
import {
  DottedPattern,
  OrangeDiagonal,
  TealCircle,
  YellowCircle,
} from "../components/DecorativeShapes";
import { getNavigationSections } from "../constants/appConstants";
import { useSettingsStore } from "../stores/settingsStore";

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const mainRef = useRef<HTMLElement>(null);

  // Reset scroll to top on every route change.
  // The <main> element is the actual scroll container for all pages.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [location.pathname]);
  const { t } = useTranslation();
  const { darkMode, toggleDarkMode, toggleLanguage, language } =
    useSettingsStore(
      useShallow((state) => ({
        darkMode: state.darkMode,
        toggleDarkMode: state.toggleDarkMode,
        toggleLanguage: state.toggleLanguage,
        language: state.language,
      })),
    );
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const [expandedSections, setExpandedSections] = useState<{
    application: boolean;
    administration: boolean;
  }>({
    application: true,
    administration: false,
  });

  // Debug: Log user data to see if parent_agency_name and parent_org_name are present
  if (import.meta.env.DEV && user) {
    console.log("Current user data:", user);
    console.log("parent_agency_name:", user.parent_agency_name);
    console.log("parent_org_name:", user.parent_org_name);
  }

  const navigationSections = getNavigationSections(t);

  const toggleSection = (sectionId: "application" | "administration") => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div
      className={`h-screen flex relative overflow-hidden transition-colors duration-300 ${user?.scope ? `scope-${user.scope}` : ""} ${
        darkMode ? "bg-gray-900" : "bg-[var(--brand-gray-light)]"
      }`}
    >
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-48 h-48 lg:w-72 lg:h-72 pointer-events-none opacity-70 z-0">
        <OrangeDiagonal className="w-full h-full" />
      </div>

      <div className="absolute top-20 -right-20 lg:-right-32 w-64 h-64 lg:w-96 lg:h-96 pointer-events-none z-0">
        <TealCircle className="w-full h-full" />
      </div>

      <div className="absolute top-32 right-12 lg:right-24 w-56 h-56 lg:w-80 lg:h-80 pointer-events-none z-0">
        <YellowCircle className="w-full h-full" />
      </div>

      <div className="absolute top-16 right-16 w-3 h-3 lg:w-4 lg:h-4 bg-[var(--brand-secondary)] rounded-full pointer-events-none z-0"></div>
      <div className="absolute top-24 right-64 w-2 h-2 bg-[var(--brand-secondary)] rounded-full pointer-events-none z-0 hidden lg:block"></div>
      <div className="absolute bottom-32 left-32 w-3 h-3 bg-[var(--brand-secondary)] rounded-full pointer-events-none z-0"></div>

      <div className="absolute top-24 left-24 w-16 h-16 text-gray-900 pointer-events-none opacity-20 z-0">
        <DottedPattern className="w-full h-full" />
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-60 transform transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        } ${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"} border-r flex flex-col`}
      >
        {/* Logo */}
        <div className="py-3.5 px-6 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <Link to="/app/dashboard" className="flex items-center gap-3">
              <img
                src={darkMode ? "/logo.svg" : "/logo-dark.svg"}
                alt="FiveVai Logo"
                className="h-10 w-auto"
              />
            </Link>
            <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
              <X
                className={`w-6 h-6 ${darkMode ? "text-gray-400" : "text-gray-600"}`}
              />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav
          className="flex-1 overflow-y-auto p-4 space-y-1"
          style={{ scrollbarGutter: "stable" }}
        >
          {navigationSections.map((section) => {
            const isExpanded =
              expandedSections[section.id as "application" | "administration"];
            // Per-section accent: Application → teal (藍色 ai-iro), IAM → amber (琥珀色 kohaku-iro)
            const isApp = section.id === "application";
            const accentColor = isApp
              ? "var(--brand-primary)"
              : "var(--brand-secondary)";
            const accentBorderClass = isApp
              ? "border-[var(--brand-primary)]"
              : "border-[var(--brand-secondary)]";
            const accentDotClass = isApp
              ? "bg-[var(--brand-primary)]"
              : "bg-[var(--brand-secondary)]";
            const accentBgExpandedDark = isApp
              ? "bg-[var(--brand-primary)]/15 text-[var(--brand-primary)]"
              : "bg-[var(--brand-secondary)]/15 text-[var(--brand-secondary)]";
            const accentBgExpandedLight = isApp
              ? "bg-[var(--brand-primary)]/15 text-teal-700"
              : "bg-[var(--brand-secondary)]/15 text-amber-700";

            return (
              <div
                key={section.id}
                className={`mb-3 rounded-xl border-l-2 transition-colors duration-300 ${
                  isExpanded
                    ? `${accentBorderClass} ${darkMode ? "bg-gray-800/40" : "bg-white/60"}`
                    : `border-transparent ${darkMode ? "bg-gray-800/20" : "bg-white/40"}`
                }`}
              >
                <button
                  onClick={() =>
                    toggleSection(
                      section.id as "application" | "administration",
                    )
                  }
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-[10px] font-bold tracking-widest uppercase transition-all duration-200 ${
                    darkMode ? accentBgExpandedDark : accentBgExpandedLight
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {/* Colored accent dot — subtle kanji-circle aesthetic */}
                    <span
                      className={`w-1.5 h-1.5 rounded-full transition-opacity duration-300 ${accentDotClass} ${isExpanded ? "opacity-100" : "opacity-30"}`}
                    />
                    {section.title}
                  </span>
                  <ChevronDown
                    style={{ color: isExpanded ? accentColor : undefined }}
                    className={`w-3.5 h-3.5 transition-transform duration-300 ease-in-out ${
                      isExpanded ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Animated accordion using grid-rows trick */}
                <div
                  className={`grid transition-[grid-template-rows,opacity] duration-400 ease-in-out ${
                    isExpanded
                      ? "grid-rows-[1fr] opacity-100"
                      : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden transition-none">
                    <div className="mt-1 space-y-0.5 pb-1 pl-1">
                      {section.items.map((item) => (
                        <Can
                          key={item.id}
                          permissions={item.permissions}
                          fallback={null}
                        >
                          <Link
                            to={item.path}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 ${
                              location.pathname === item.path
                                ? darkMode
                                  ? `bg-[${accentColor}]/20 text-[${accentColor}] font-semibold`
                                  : `shadow-sm text-white`
                                : darkMode
                                  ? "text-gray-300 hover:bg-gray-700 hover:text-white"
                                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                            }`}
                            style={
                              location.pathname === item.path
                                ? darkMode
                                  ? {
                                      backgroundColor: `color-mix(in srgb, ${accentColor} 15%, transparent)`,
                                      color: accentColor,
                                    }
                                  : { backgroundColor: accentColor }
                                : undefined
                            }
                            onClick={() => setSidebarOpen(false)}
                          >
                            <item.icon className="w-5 h-5 shrink-0" />
                            <span className="text-sm font-semibold">
                              {item.label}
                            </span>
                          </Link>
                        </Can>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        {/* User Profile Section */}
        <div
          className={`p-4 border-t ${darkMode ? "border-gray-700" : "border-gray-100"}`}
        >
          <div
            className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
              darkMode
                ? "bg-gray-700 hover:bg-gray-600"
                : "bg-gray-50 hover:bg-gray-100"
            }`}
          >
            <div className="w-10 h-10 bg-[var(--brand-primary)] rounded-full flex items-center justify-center text-white font-bold">
              {user?.first_name?.charAt(0) || user?.name?.charAt(0) || "A"}
            </div>
            <div className="flex-1 min-w-0">
              <p
                className={`text-sm font-medium truncate ${darkMode ? "text-white" : "text-gray-900"}`}
              >
                {user?.first_name || "-"}
              </p>
              <p
                className={`text-xs truncate ${darkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {user?.role || "Global Admin"}
              </p>
            </div>
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen((prev) => !prev)}
                className={`p-1 rounded-lg transition-colors ${
                  darkMode ? "hover:bg-gray-600" : "hover:bg-gray-200"
                }`}
              >
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${userMenuOpen ? "rotate-180" : ""} ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                />
              </button>
              {/* Dropdown Menu */}
              {userMenuOpen && (
                <div
                  className={`absolute bottom-full right-0 mb-2 w-48 rounded-xl shadow-xl border py-2 z-50 ${
                    darkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate("/app/dashboard");
                    }}
                    className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                      darkMode
                        ? "text-gray-300 hover:bg-gray-700"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <Settings className="w-4 h-4" />
                    {t("nav.iamSettings")}
                  </button>
                  <div
                    className={`border-t my-1 ${darkMode ? "border-gray-700" : "border-gray-100"}`}
                  ></div>
                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      handleLogout();
                    }}
                    className={`w-full px-4 py-2 text-left text-sm text-[var(--brand-secondary)] flex items-center gap-2 ${
                      darkMode ? "hover:bg-gray-700" : "hover:bg-orange-50"
                    }`}
                  >
                    <LogOut className="w-4 h-4" />
                    {t("auth.logout")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay for user menu close */}
      {userMenuOpen && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Close menu"
          className="fixed inset-0 z-40"
          onClick={() => setUserMenuOpen(false)}
          onKeyDown={(e) => e.key === "Escape" && setUserMenuOpen(false)}
        />
      )}

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Close sidebar"
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          onKeyDown={(e) => e.key === "Escape" && setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header
          className={`relative z-10 ${
            darkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-200"
          } border-b px-4 lg:px-8 py-4`}
        >
          <div className="flex items-center justify-between gap-4">
            {/* Mobile menu button */}
            <button
              className={`lg:hidden ${darkMode ? "text-gray-400" : "text-gray-600"}`}
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Agency and Organization Info */}
            <div className="flex-1 flex items-center gap-2 ml-4">
              {user?.parent_agency_name && (
                <div
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
                    darkMode
                      ? "bg-[var(--brand-primary)]/10 border-[var(--brand-primary)]/30"
                      : "bg-[var(--brand-primary)]/5 border-[var(--brand-primary)]/20"
                  }`}
                >
                  <Building2 className="w-3.5 h-3.5 text-[var(--brand-primary)]" />
                  <span
                    className={`text-sm font-medium ${darkMode ? "text-gray-200" : "text-gray-800"}`}
                  >
                    {user.parent_agency_name}
                  </span>
                </div>
              )}
              {user?.parent_org_name && (
                <div
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
                    darkMode
                      ? "bg-[var(--brand-secondary)]/10 border-[var(--brand-secondary)]/30"
                      : "bg-[var(--brand-secondary)]/5 border-[var(--brand-secondary)]/20"
                  }`}
                >
                  <Building2 className="w-3.5 h-3.5 text-[var(--brand-secondary)]" />
                  <span
                    className={`text-sm font-medium ${darkMode ? "text-gray-200" : "text-gray-800"}`}
                  >
                    {user.parent_org_name}
                  </span>
                </div>
              )}
            </div>

            {/* Right side actions */}
            <div className="flex items-center gap-2">
              {/* Context switcher — only shown when user has multiple contexts */}
              <ContextSwitcherMenu />

              {/* Language switcher */}
              <div
                className={`flex items-center gap-1 px-3 py-2 rounded-lg ${
                  darkMode ? "bg-gray-700" : "bg-gray-100"
                }`}
              >
                <button
                  onClick={() => language !== "en" && toggleLanguage()}
                  className={`text-sm font-medium transition-colors ${
                    language === "en"
                      ? darkMode
                        ? "text-white"
                        : "text-gray-900"
                      : darkMode
                        ? "text-gray-500 hover:text-gray-300"
                        : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  EN
                </button>
                <span
                  className={`text-sm ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                >
                  |
                </span>
                <button
                  onClick={() => language !== "ja" && toggleLanguage()}
                  className={`text-sm font-medium transition-colors ${
                    language === "ja"
                      ? darkMode
                        ? "text-white"
                        : "text-gray-900"
                      : darkMode
                        ? "text-gray-500 hover:text-gray-300"
                        : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  日本語
                </button>
              </div>

              {/* Dark mode toggle */}
              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-lg transition-colors ${
                  darkMode
                    ? "text-gray-300 hover:bg-gray-700"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {darkMode ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>

              {/* Logout */}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main ref={mainRef} className="flex-1 overflow-y-auto relative z-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
